/**
 * Model selection — UI-side allowlist and label mapping.
 *
 * The TypeScript union is a UX affordance only; the server should re-validate
 * independently against its own closed allowlist. A tampered client posting an
 * arbitrary modelId should get a fixed error from the backend, never an
 * arbitrary attacker-controlled model id.
 */

// Keep this list in lockstep with whatever model allowlist your backend
// enforces server-side, so the UI only offers models the server will accept.
export type AnalystModelId =
  | "us.anthropic.claude-sonnet-5"
  | "us.anthropic.claude-sonnet-4-6"
  | "us.anthropic.claude-opus-4-8"

// Default model. The supervisor runs on Claude Sonnet 4.6 (see the agentcore
// stack / agent config), so that's the only option offered today; keep this in
// lockstep with the backend. Add more entries below once the backend can honor
// a per-turn model selection.
export const DEFAULT_MODEL: AnalystModelId = "us.anthropic.claude-sonnet-4-6"

export interface ModelOption {
  id: AnalystModelId
  label: string
  shortLabel: string
}

/**
 * Ordered list of selectable models (display order in the dropdown). Only the
 * model the supervisor actually runs is offered, so the selector is truthful;
 * add options here when the backend supports switching.
 */
export const MODEL_OPTIONS: ModelOption[] = [
  {
    id: "us.anthropic.claude-sonnet-4-6",
    label: "Sonnet 4.6",
    shortLabel: "S4.6",
  },
]

/**
 * Reasoning-effort selection — UI-side allowlist and label mapping.
 *
 * These map to Claude adaptive thinking (thinking.type="adaptive" +
 * output_config.effort). The supervisor runs on Sonnet 4.6, which supports only
 * low/medium/high — "max"/"xhigh" are Opus-only and would 400 against Sonnet, so
 * they are not offered. Like AnalystModelId, this union is a UX affordance only;
 * the backend re-validates against its own closed allowlist.
 */
export type AnalystEffort = "low" | "medium" | "high"

// Keep this equal to the backend's default effort (agent config `effort: high`,
// Anthropic's default). The client OMITS effort from the request when the
// selection equals this default, so a default turn sends no effort and the agent
// applies its own default — see ChatInterface / client.invoke.
export const DEFAULT_EFFORT: AnalystEffort = "high"

export interface EffortOption {
  id: AnalystEffort
  label: string
}

/** Ordered list of selectable effort levels (display order in the dropdown). */
export const EFFORT_OPTIONS: EffortOption[] = [
  { id: "low", label: "Bajo" },
  { id: "medium", label: "Medio" },
  { id: "high", label: "Alto" },
]
